import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Copy, CheckCircle, Warning, ArrowsClockwise, DownloadSimple, PlugsConnected } from "@phosphor-icons/react";
import { createPairingCode, listAgents, listInstallers, pairingLink } from "@/lib/agent";
import { useT } from "@/lib/i18n";

const SERVER_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");

/** Sistema del equipo con la impresora; se adivina por el navegador y se puede cambiar. */
function detectOs() {
    const ua = `${navigator.userAgent} ${navigator.platform || ""}`;
    if (/Windows|Win32|Win64/i.test(ua)) return "windows";
    if (/Linux|X11/i.test(ua) && !/Android/i.test(ua)) return "linux";
    return "mac";
}

const OS_OPTIONS = [
    { id: "mac", label: "Mac" },
    { id: "windows", label: "Windows" },
    { id: "linux", label: "Linux" },
];

/**
 * Empareja un Mac con el servidor: muestra un código de un solo uso (10 min) y los 3 comandos
 * para pegar en Terminal. Detecta solo cuando el agente se empareja.
 */
export default function ConnectAgentModal({ open, onClose, onConnected }) {
    const t = useT();
    const [installers, setInstallers] = useState(null); // qué instaladores ofrece el servidor (null = aún no se sabe)
    const [linkTried, setLinkTried] = useState(false);
    const [pairing, setPairing] = useState(null); // { code, expiresInSec }
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [error, setError] = useState("");
    const [connected, setConnected] = useState(null); // agente recién emparejado
    const [copied, setCopied] = useState(false);
    const [os, setOs] = useState(detectOs);
    const baseline = useRef(new Set());

    const generate = useCallback(async () => {
        setError("");
        setConnected(null);
        setCopied(false);
        try {
            baseline.current = new Set((await listAgents()).map((a) => a.id));
            const p = await createPairingCode();
            setPairing(p);
            setSecondsLeft(p.expiresInSec);
        } catch (e) {
            setPairing(null);
            setError(e?.response?.data?.detail || "No se pudo generar el código");
        }
    }, []);

    useEffect(() => {
        if (open) generate();
    }, [open, generate]);

    useEffect(() => {
        if (!open) return;
        listInstallers().then(setInstallers).catch(() => setInstallers(null)); // sin instaladores: solo se ofrecen los comandos
    }, [open]);

    // cuenta regresiva
    useEffect(() => {
        if (!open || !pairing || connected) return undefined;
        const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(id);
    }, [open, pairing, connected]);

    // detectar el agente nuevo (depende de "venció", no del contador: si no, el intervalo se reiniciaría cada segundo)
    const timedOut = pairing !== null && secondsLeft === 0;
    useEffect(() => {
        if (!open || !pairing || connected || timedOut) return undefined;
        const id = setInterval(async () => {
            try {
                const fresh = (await listAgents()).find((a) => !baseline.current.has(a.id));
                if (fresh) {
                    setConnected(fresh);
                    onConnected?.(fresh);
                }
            } catch {
                /* se reintenta */
            }
        }, 2000);
        return () => clearInterval(id);
    }, [open, pairing, connected, timedOut, onConnected]);

    if (!open) return null;

    const isWindows = os === "windows";
    const py = isWindows ? "py" : "python3";
    const curl = isWindows ? "curl.exe" : "curl"; // en PowerShell "curl" es otra cosa: hay que decir curl.exe
    const shellName = isWindows ? "PowerShell" : "Terminal";
    const commands = pairing
        ? [
              `${curl} -fsSL -o zebralab_agent.py ${SERVER_URL}/api/agent/download`,
              `${py} zebralab_agent.py pair --server ${SERVER_URL} --code ${pairing.code}`,
              `${py} zebralab_agent.py install`,
          ].join("\n")
        : "";
    const openHint = isWindows
        ? "Abre PowerShell (menú Inicio, escribe PowerShell) en el PC que tiene la Zebra y pega estos comandos. Solo se hace una vez."
        : os === "linux"
          ? "Abre una terminal en el equipo que tiene la Zebra y pega estos comandos. Solo se hace una vez."
          : "Abre la app Terminal en el Mac que tiene la Zebra y pega estos comandos. Solo se hace una vez.";
    const pythonHint = isWindows
        ? "Necesita Python 3. Si PowerShell no reconoce «py», instálalo desde python.org o la Microsoft Store."
        : os === "linux"
          ? "Necesita Python 3 y CUPS (casi siempre ya vienen instalados)."
          : "Necesita Python 3, que ya viene con el Mac.";
    const expired = pairing && secondsLeft === 0 && !connected;
    const installer = installers?.[os] || null; // la app instalable de este sistema, si el servidor la tiene
    const sizeMb = installer ? `${Math.max(1, Math.round(installer.size / 1048576))} MB` : "";
    const osLabel = OS_OPTIONS.find((o) => o.id === os)?.label || "";
    const Manual = installer ? "details" : "div"; // con la app disponible, los comandos quedan plegados; si no, son lo principal
    const mm = String(Math.floor(secondsLeft / 60)).padStart(1, "0");
    const ss = String(secondsLeft % 60).padStart(2, "0");

    async function copy() {
        try {
            await navigator.clipboard.writeText(commands);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            setError("No pude copiar automáticamente. Selecciona los comandos y cópialos a mano.");
        }
    }

    function connectWithApp() {
        setLinkTried(true);
        window.location.href = pairingLink(SERVER_URL, pairing.code);
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-950/50 p-6">
            <div className="bg-white w-full max-w-xl max-h-[92vh] overflow-auto flex flex-col shadow-2xl border border-brand-300">
                <div className="px-5 py-3.5 border-b border-brand-200 bg-brand-50/60 flex items-center justify-between">
                    <h2 className="font-headings text-lg font-semibold text-brand-900">Conectar agente de impresión</h2>
                    <button data-testid="connect-close" onClick={onClose} className="p-1.5 text-brand-700 hover:bg-brand-100">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 text-sm text-brand-900">
                    {connected ? (
                        <div data-testid="connect-success" className="bg-green-50 border border-green-300 text-green-900 px-4 py-3 flex items-start gap-2">
                            <CheckCircle size={20} className="shrink-0 mt-0.5" weight="fill" />
                            <div>
                                <p className="font-medium">¡Agente conectado!</p>
                                <p className="text-xs mt-0.5">
                                    «{connected.name}» quedó emparejado. Si ejecutaste el comando <code className="font-mono">install</code>,
                                    arrancará solo cada vez que inicies sesión en el Mac.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2" role="tablist" aria-label="Sistema del equipo con la impresora">
                                <span className="text-[10px] font-semibold text-brand-800 uppercase tracking-wider">Sistema</span>
                                {OS_OPTIONS.map((o) => (
                                    <button
                                        key={o.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={os === o.id}
                                        data-testid={`connect-os-${o.id}`}
                                        onClick={() => setOs(o.id)}
                                        className={`px-3 py-1 text-xs font-medium border ${
                                            os === o.id
                                                ? "bg-brand-900 text-white border-brand-900"
                                                : "bg-white text-brand-900 border-brand-300 hover:bg-brand-100"
                                        }`}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                            {installer && (
                                <div data-testid="connect-app" className="border border-brand-300 bg-brand-50 p-4 space-y-3" translate="no">
                                    <h3 className="font-headings font-semibold text-brand-900">{t("connect.app.title")}</h3>
                                    <div>
                                        <p className="font-medium mb-1.5">{t("connect.app.step1")}</p>
                                        <a
                                            data-testid="connect-app-download"
                                            href={`${SERVER_URL}/api/agent/installer/${os}`}
                                            title={`SHA-256 ${installer.sha256}`}
                                            className="inline-flex items-center gap-2 px-3 py-2 bg-brand-900 text-white text-sm font-medium hover:bg-brand-800"
                                        >
                                            <DownloadSimple size={16} />
                                            <span>{t("connect.app.download", { os: osLabel, version: installer.version, size: sizeMb })}</span>
                                        </a>
                                        <p className="text-[12px] text-brand-700 mt-1.5">{t(`connect.app.unsigned.${os}`)}</p>
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1.5">{t("connect.app.step2")}</p>
                                        <button
                                            data-testid="connect-app-open"
                                            onClick={connectWithApp}
                                            disabled={!pairing || expired}
                                            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-brand-900 text-brand-900 text-sm font-medium hover:bg-brand-100 disabled:opacity-50"
                                        >
                                            <PlugsConnected size={16} />
                                            <span>{t("connect.app.connect")}</span>
                                        </button>
                                        <p className="text-[12px] text-brand-700 mt-1.5">{t("connect.app.hint")}</p>
                                        {linkTried && <p data-testid="connect-app-nothing" className="text-[12px] text-brand-700 mt-1">{t("connect.app.nothing")}</p>}
                                    </div>
                                </div>
                            )}
                            {!installer && (
                                <p data-testid="connect-open-hint" className="text-brand-800">
                                    {openHint}
                                </p>
                            )}

                            <div>
                                <label className="text-[10px] font-semibold text-brand-800 uppercase tracking-wider mb-1.5 block">
                                    Código de emparejamiento
                                </label>
                                <div className="flex items-center gap-3">
                                    <div
                                        data-testid="connect-code"
                                        className={`font-mono text-2xl tracking-[0.25em] px-4 py-2 border ${
                                            expired ? "border-red-300 text-red-700 bg-red-50 line-through" : "border-brand-300 bg-brand-50 text-brand-950"
                                        }`}
                                    >
                                        {pairing ? pairing.code : "····-····"}
                                    </div>
                                    {pairing && !expired && (
                                        <span data-testid="connect-countdown" className="text-xs font-mono text-brand-700">
                                            {`vence en ${mm}:${ss}`}
                                        </span>
                                    )}
                                    {(expired || error) && (
                                        <button
                                            data-testid="connect-regenerate"
                                            onClick={generate}
                                            className="px-3 py-1.5 text-xs bg-brand-900 text-white hover:bg-brand-800 flex items-center gap-1.5 font-medium"
                                        >
                                            <ArrowsClockwise size={14} /> Código nuevo
                                        </button>
                                    )}
                                </div>
                            </div>

                            <Manual>
                            {installer && (
                                <summary data-testid="connect-manual" className="cursor-pointer text-brand-800 text-sm mb-2" translate="no">
                                    {t("connect.manual", { shell: shellName })}
                                </summary>
                            )}
                            {installer && <p data-testid="connect-open-hint" className="text-brand-800 mb-3">{openHint}</p>}
                            <div>
                                <label className="text-[10px] font-semibold text-brand-800 uppercase tracking-wider mb-1.5 block">
                                    {`Comandos (${shellName})`}
                                </label>
                                <pre
                                    data-testid="connect-commands"
                                    className="bg-brand-950 text-brand-100 p-3 font-mono text-[12px] leading-relaxed overflow-auto whitespace-pre-wrap break-all"
                                >
                                    {commands || "…"}
                                </pre>
                                <div className="mt-2 flex items-center gap-3">
                                    <button
                                        data-testid="connect-copy"
                                        onClick={copy}
                                        disabled={!pairing || expired}
                                        className="px-3 py-1.5 text-xs bg-white border border-brand-300 text-brand-900 hover:bg-brand-100 flex items-center gap-1.5 font-medium disabled:opacity-50"
                                    >
                                        {copied ? <CheckCircle size={14} /> : <Copy size={14} />} {copied ? "Copiado" : "Copiar comandos"}
                                    </button>
                                    <span className="text-[11px] text-brand-700">Esta ventana avisa sola cuando el agente se conecte.</span>
                                </div>
                            </div>

                            <ul className="text-[12px] text-brand-700 list-disc pl-5 space-y-1">
                                <li>El último comando hace que el agente arranque solo al iniciar sesión.</li>
                                <li>Después puedes borrar el archivo <code className="font-mono">zebralab_agent.py</code> descargado.</li>
                                <li data-testid="connect-python-hint">{pythonHint}</li>
                            </ul>
                            </Manual>
                        </>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-300 text-red-800 px-3 py-2 flex items-start gap-2">
                            <Warning size={18} className="shrink-0 mt-0.5" />
                            <div>{error}</div>
                        </div>
                    )}
                </div>

                <div className="px-5 py-3.5 border-t border-brand-200 bg-brand-50/60 flex justify-end">
                    <button onClick={onClose} className="px-4 py-2 bg-white border border-brand-300 text-brand-900 text-sm font-medium hover:bg-brand-100">
                        {connected ? "Listo" : "Cerrar"}
                    </button>
                </div>
            </div>
        </div>
    );
}
